import { useEffect, useRef, useState, type ReactNode } from 'react';
import './InfoTip.css';

interface InfoTipProps {
  /** Plain single-line tooltip text. */
  text?: string;
  /** Rich tooltip content (wrapping, left-aligned, wider popup). Takes
   *  precedence over `text` when provided. */
  children?: ReactNode;
}

/**
 * Hover to peek, click to pin. A pointer that rests on the icon shows the tip
 * and leaving hides it; a click (or a tap, where there is no hover) keeps it
 * open until the next click, a click outside, or Escape.
 */
export function InfoTip({ text, children }: InfoTipProps) {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const open = pinned || hovered;
  const close = () => { setPinned(false); setHovered(false); };
  const ref = useRef<HTMLSpanElement>(null);

  // Dismiss on click/tap outside the tip, or on Escape. Without this the
  // popup stays open until its icon is clicked again, and multiple tips can
  // linger open at once.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      className="info-tip"
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="info-tip-icon" onClick={() => { setPinned(v => !v); setHovered(false); }}>i</span>
      {open && (
        <span className={`info-tip-popup${children ? ' info-tip-popup--rich' : ''}`}>
          {children ?? text}
        </span>
      )}
    </span>
  );
}
